import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

interface VendorGstRow {
  readonly vendorId: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  readonly vendorGstin: string;
  readonly invoiceCount: number;
  readonly taxableAmount: number;
  readonly cgst: number;
  readonly sgst: number;
  readonly igst: number;
  readonly cess: number;
  readonly otherTax: number;
  /** Tax the vendor charged (forward charge); CGST..Cess are its split. */
  readonly forwardChargeTax: number;
  /** Tax on reverse-charge lines, payable by the company itself. */
  readonly reverseChargeTax: number;
  readonly totalAmount: number;
}

interface Summary {
  readonly from: string;
  readonly to: string;
  readonly rows: readonly VendorGstRow[];
  readonly totals: Omit<
    VendorGstRow,
    'vendorId' | 'vendorCode' | 'vendorName' | 'vendorGstin'
  >;
}

const CELL: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--color-border-default, #d0d7de)',
  textAlign: 'left',
};
const NUM: React.CSSProperties = { ...CELL, textAlign: 'right' };
const INPUT: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid var(--color-border-default, #d0d7de)',
};


/** Indian financial year starts in April, which is the period accounts reports on. */
function financialYearStart(): string {
  const now = new Date();
  const y = now.getUTCMonth() >= 3 ? now.getUTCFullYear() : now.getUTCFullYear() - 1;
  return `${y}-04-01`;
}

export function VendorGstReportPage(): ReactNode {
  const { token } = useAuth();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  const [from, setFrom] = useState(financialYearStart());
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setData(
        await apiRequest<Summary>(
          `/v1/billing/gst/by-vendor?from=${from}&to=${to}`,
          { token },
        ),
      );
    } catch (err) {
      setError((err as Error).message);
      setData(null);
    } finally {
      setBusy(false);
    }
  }, [from, to, token]);

  useEffect(() => {
    void load();
    // Loaded once; the period is applied when the user asks for it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = data?.totals;

  return (
    <main aria-label="vendor-gst-report">
      <h1 style={{ marginBottom: 4 }}>GST by vendor</h1>
      <p style={{ marginTop: 0, color: 'var(--color-text-muted, #656d76)' }}>
        Tax accepted from each vendor on bills dated in this period. Draft,
        withdrawn and rejected bills are excluded — nothing was owed on them.
        Reverse-charge tax is shown apart: the company pays it to the
        government, so it is neither charged by nor paid to the vendor.
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap' }}>
        <label>
          <div style={{ fontSize: 13, fontWeight: 600 }}>From</div>
          <DateInput type="date" style={INPUT} value={from} onValueChange={(nextValue) => setFrom(nextValue)} data-testid="gst-from" />
        </label>
        <label>
          <div style={{ fontSize: 13, fontWeight: 600 }}>To</div>
          <DateInput type="date" style={INPUT} value={to} min={from} onValueChange={(nextValue) => setTo(nextValue)} data-testid="gst-to" />
        </label>
        <button type="button" onClick={() => void load()} disabled={busy} data-testid="gst-apply"
          style={{ padding: '8px 18px', borderRadius: 6, border: 'none', cursor: busy ? 'not-allowed' : 'pointer',
            background: 'var(--color-brand-primary, #0969da)', color: 'var(--color-text-inverse)', fontWeight: 600 }}>
          {busy ? 'Loading…' : 'Apply'}
        </button>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}

      {data && data.rows.length === 0 && (
        <p data-testid="gst-empty">No bill was dated in this period.</p>
      )}

      {data && data.rows.length > 0 && (
        <table data-testid="gst-by-vendor" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={CELL}>Vendor</th>
              <th style={CELL}>GSTIN</th>
              <th style={NUM}>Bills</th>
              <th style={NUM}>Taxable (₹)</th>
              <th style={NUM}>CGST (₹)</th>
              <th style={NUM}>SGST (₹)</th>
              <th style={NUM}>IGST (₹)</th>
              <th style={NUM}>Cess (₹)</th>
              <th style={NUM}>Tax charged by vendor (₹)</th>
              <th style={NUM}>Reverse charge — payable by recipient (₹)</th>
              <th style={NUM}>Payable to vendor (₹)</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.vendorId} data-testid={`gst-row-${r.vendorCode || r.vendorId}`}>
                <td style={CELL}>
                  <div>{r.vendorName || '\u2014'}</div>
                  <div style={{ color: 'var(--color-text-muted, #656d76)' }}>{r.vendorCode}</div>
                </td>
                <td style={CELL}>{r.vendorGstin || '\u2014'}</td>
                <td style={NUM}>{r.invoiceCount}</td>
                <td style={NUM}>{money(r.taxableAmount)}</td>
                <td style={NUM}>{money(r.cgst)}</td>
                <td style={NUM}>{money(r.sgst)}</td>
                <td style={NUM}>{money(r.igst)}</td>
                <td style={NUM}>{money(r.cess)}</td>
                <td style={NUM}>{money(r.forwardChargeTax)}</td>
                <td style={NUM} data-testid={`gst-rcm-${r.vendorCode || r.vendorId}`}>{money(r.reverseChargeTax)}</td>
                <td style={NUM}>{money(r.totalAmount)}</td>
              </tr>
            ))}
          </tbody>
          {t && (
            <tfoot>
              <tr data-testid="gst-totals" style={{ fontWeight: 700 }}>
                <td style={CELL} colSpan={2}>Total</td>
                <td style={NUM}>{t.invoiceCount}</td>
                <td style={NUM}>{money(t.taxableAmount)}</td>
                <td style={NUM}>{money(t.cgst)}</td>
                <td style={NUM}>{money(t.sgst)}</td>
                <td style={NUM}>{money(t.igst)}</td>
                <td style={NUM}>{money(t.cess)}</td>
                <td style={NUM}>{money(t.forwardChargeTax)}</td>
                <td style={NUM}>{money(t.reverseChargeTax)}</td>
                <td style={NUM}>{money(t.totalAmount)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </main>
  );
}
